import { mergeStyleSets } from "@uifabric/merge-styles";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { formatNumber } from "utils/number";
import { useSelector } from "../../../store/hooks";
import { formatDateWithMoment } from "../../../utils/utils";
import FormItemTitle from "../FormItemTitle";
import { formatPreviewValue } from "../helpers";
import { vehicleConfig } from "../vehicleConfig";
import {
  CLIENT_HIDDEN_FINANCE_FIELDS,
  isClientFormUser,
} from "../clientVehicleSpec";
import RequestedOfferSummary from "./RequestedOfferSummary";

const getClassNames = (theme: any) =>
  mergeStyleSets({
    container: {
      padding: 40,
    },
    gridRoot: {
      display: "grid",
      margin: "15px 0px 0px 50px",
      gridTemplateColumns: "50% 50%",
    },
    previewLabel: {
      fontWeight: 600,
      fontSize: "14px",
      marginTop: "10px",
      color: theme.palette.black,
    },
    previewValue: {
      fontWeight: "normal",
      color: theme.palette.black,
    },
    isMarked: {
      background: theme.palette.yellowLight,
      color: theme.palette.black,
    },
    coverageContainer: {
      marginTop: "5px",
    },
    coverageItem: {
      marginBottom: "6px",
      paddingLeft: "12px",
    },
    coverageName: {
      fontWeight: 600,
      marginBottom: "2px",
    },
    coverageDates: {
      fontSize: "12px",
      color: theme.palette.neutralSecondary,
    },
  });

const FinancePreview = ({ vehicle, route }) => {
  const theme = useTheme() as IStyledTheme;
  const { t } = useTranslation();
  const taxonomy = useSelector((s) => s.taxonomy);
  const appSettings = useSelector((s) => s.vehicle.vehicleAppSettings);
  const userRole = useSelector((s) => s.auth.userRole);
  const vehicleLastStatusDateVisible = false; //route === "createVehicle" || route === "duplicateVehicle";
  const isTradeLicensePlate = vehicle.isTradeLicensePlate;
  const isClientForm = isClientFormUser(appSettings?.COUNTRY_CODE, userRole);

  const classes = getClassNames(theme);

  // Helper function to check if field should be visible
  const isFieldVisible = (fieldName: string) => {
    // Hide broker-only finance fields for the external client's read-only view.
    if (isClientForm && CLIENT_HIDDEN_FINANCE_FIELDS.has(fieldName)) {
      return false;
    }
    return appSettings?.VISIBLE_FIELDS?.includes(fieldName) ?? true;
  };

  // Helper function to render client comment as coverage information
  const renderClientCommentCoverages = (clientComment: string) => {
    try {
      const parsed = JSON.parse(clientComment);
      const coverages = [];

      Object.keys(parsed).forEach((domain) => {
        const coverage = parsed[domain];
        if (coverage.Enabled) {
          const coverageName = t(domain);
          let dateRange = "";

          if (coverage.CoverageFrom || coverage.CoverageTo) {
            const fromDate = coverage.CoverageFrom
              ? formatDateWithMoment(coverage.CoverageFrom)
              : "";
            const toDate = coverage.CoverageTo
              ? formatDateWithMoment(coverage.CoverageTo)
              : "";

            if (fromDate && toDate) {
              dateRange = ` (${fromDate} - ${toDate})`;
            } else if (fromDate) {
              dateRange = ` (${t("bfm.from.label")}: ${fromDate})`;
            } else if (toDate) {
              dateRange = ` (${t("bfm.to.label")}: ${toDate})`;
            }
          }

          const urgent = coverage.Urgent ? ` - ${t("bfm.urgent.label")}` : "";
          coverages.push({ name: coverageName, dateRange, urgent });
        }
      });

      if (coverages.length === 0) {
        return t("bfm.noCoverages.label");
      }

      return (
        <div className={classes.coverageContainer}>
          {coverages.map((coverage, index) => (
            <div key={index} className={classes.coverageItem}>
              <div className={classes.coverageName}>
                {coverage.name}
                {coverage.urgent}
              </div>
              {coverage.dateRange && (
                <div className={classes.coverageDates}>
                  {coverage.dateRange}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    } catch {
      // If JSON parsing fails, return original value
      return clientComment;
    }
  };

  const initialValues = useMemo(() => {
    return vehicleConfig({
      vehicle,
      vehicleAppSettings: appSettings,
      route,
      t,
      taxonomy,
    })
      .map((configItem: any) => {
        let value = configItem?.value?.label || configItem.value;

        // Special handling for clientCoveragesInfo when HAS_CLIENT_COMMENT_EXTERNAL_USER is true
        if (
          configItem.name === "clientCoveragesInfo" &&
          appSettings?.HAS_CLIENT_COMMENT_EXTERNAL_USER &&
          configItem.value
        ) {
          return {
            value: renderClientCommentCoverages(configItem.value),
            key: "bfm.coverages.label", // Use special label
            tab: configItem.tab,
            visibleInTradeLicensePlate: configItem.visibleInTradeLicensePlate,
            isMarked: configItem.editedByExternalUser,
            fieldName: configItem.name,
          };
        }
        if (configItem.field === "radio-group") {
          value =
            value === null
              ? t("greco.notDefined")
              : value
                ? t("greco.yes")
                : t("greco.no");
        }

        if (configItem.field === "date" && configItem.value) {
          value = formatDateWithMoment(value);
        }
        if (
          (configItem.field === "number" || configItem.field === "double") &&
          configItem.value &&
          !configItem?.excludeFromPreviewFormat
        ) {
          value = formatNumber(value);

          if (value % 1 === 0 && configItem.field === "double") {
            value = `${value},00`;
          }
        }
        return {
          value,
          key: configItem.key,
          tab: configItem.tab,
          visibleInTradeLicensePlate: configItem.visibleInTradeLicensePlate,
          isMarked: configItem.editedByExternalUser,
          fieldName: configItem.name,
        };
      })
      .filter((valueObj: any) => {
        // First check if field is visible based on app settings
        if (!isFieldVisible(valueObj.fieldName)) {
          return false;
        }

        // Check if value exists (not null or undefined)
        if (valueObj.value === null || valueObj.value === undefined) {
          return false;
        }

        // Check tab visibility
        const isInFinanceTab =
          valueObj.tab === "finance-vehicle-form" ||
          (valueObj.fieldName === "clientCoveragesInfo" &&
            appSettings?.HAS_CLIENT_COMMENT_EXTERNAL_USER);

        if (!isInFinanceTab) {
          return false;
        }

        // For trade license plates, also check visibility flag
        if (isTradeLicensePlate && !valueObj.visibleInTradeLicensePlate) {
          return false;
        }

        return true;
      });
  }, [
    vehicle,
    route,
    t,
    taxonomy,
    isTradeLicensePlate,
    appSettings,
    isFieldVisible,
    renderClientCommentCoverages,
  ]);

  return (
    <div className={classes.container}>
      <FormItemTitle
        title={t("bfm.vehicleForm.tabFinance.label")}
        iconName={"Financial"}
      />
      {/* Client offer summary: the requested insurance lines + selected
          additional coverages the client entered (read-only). */}
      <RequestedOfferSummary vehicle={vehicle} />
      <div className={classes.gridRoot}>
        {initialValues.map((valueObj, index) => {
          if (
            valueObj.key === "bfm.vehicleForm.vehicleLastStatusDate.label" &&
            !vehicleLastStatusDateVisible
          ) {
            return null;
          }

          // For coverage display, value is already a React element
          const value =
            typeof valueObj.value === "string"
              ? formatPreviewValue(valueObj.value, t)
              : valueObj.value;

          return (
            <div key={valueObj.key} className={`${classes.previewLabel}`}>
              <span
                style={valueObj.isMarked ? { color: "black" } : {}}
                className={valueObj.isMarked && classes.isMarked}
              >
                {t(valueObj.key)}:{" "}
              </span>
              <div
                style={valueObj.isMarked ? { color: "black" } : {}}
                className={`${classes.previewValue} ${
                  valueObj.isMarked && classes.isMarked
                }`}
              >
                {value}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FinancePreview;
